import _ from 'lodash';
import DOMPurify from 'dompurify';
import escapeHtml from 'scripts/lib/escape_html';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Attachment from 'models/attachment';
import connect from 'components/lib/connect';
import EmailItemContext from 'components/customer/conversation_history/conversation_items_v2/email/email_item_context';
import FetchEmailAttachmentUrls from 'actions/attachments/fetch_email_attachment_urls';
import { getAttachmentUrl } from 'scripts/domain/services/attachments';
import i18n from 'scripts/i18n/i18n';
import moment from 'moment';
import ModalCard from 'components/common/modal_card';
import PortalModalWrapper from 'components/lib/portal_modal_wrapper';

export default function ViewOriginalHtmlModal({ email, timestamp }) {
  const { showHtmlModal, setShowHtmlModal } = useContext(EmailItemContext);
  const { attachments, bodyHtml } = email;

  return (
    <>
      {showHtmlModal ? (
        <HtmlContentModalContainer
          attachments={attachments}
          headers={emailHeaders(email, timestamp)}
          html={bodyHtml}
          onClose={() => setShowHtmlModal(false)}
        />
      ) : null}
    </>
  );
}

ViewOriginalHtmlModal.propTypes = {
  email: PropTypes.shape({
    attachments: PropTypes.arrayOf(PropTypes.instanceOf(Attachment)),
    bodyHtml: PropTypes.string,
  }),
  timestamp: PropTypes.string,
};

// emailHeaders picks the envelope fields the viewer shows above the body. Both the conversation email
// model and the task thread email carry these under the same names.
export function emailHeaders(email, timestamp) {
  return {
    from: email.fromName ? `${email.fromName} <${email.from}>` : email.from,
    to: email.to || [],
    cc: email.cc || [],
    bcc: email.bcc || [],
    subject: email.subject,
    timestamp,
  };
}

// Exported for callers outside the conversation item menu (e.g. the task side-thread cards), which
// need the same sandboxed-iframe rendering without EmailItemContext.
export const HtmlContentModalContainer = connect(mapStateToProps, mapExecuteToProps)(HtmlContentModal);

function mapStateToProps({ getProvider }) {
  return {
    orgId: getProvider('auth').get()?.claims?.orgId,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onFetchAttachmentUrls: (attachmentIds, onFetched) =>
      executeAction(FetchEmailAttachmentUrls, { attachmentIds, onFetched }),
  };
}

function HtmlContentModal({ attachments, headers, html, onClose, onFetchAttachmentUrls, orgId }) {
  // The sandboxed document has an opaque origin, so it can't send the session cookie to the
  // attachment route. Resolve signed URLs here and give the document those instead.
  const [urlsById, setUrlsById] = useState({});
  useEffect(() => {
    const ids = (attachments || []).map(a => a.id);
    if (!ids.length || !onFetchAttachmentUrls) return undefined;

    let cancelled = false;
    onFetchAttachmentUrls(ids, urls => !cancelled && setUrlsById(urls));
    return () => {
      cancelled = true;
    };
  }, [attachments, onFetchAttachmentUrls]);

  const convertedHtml = useMemo(
    () => wrapEmailDocument(resolveImageSources(orgId, html, attachments, urlsById), readBaseTypography(), headers),
    [attachments, headers, html, orgId, urlsById]
  );

  return (
    <PortalModalWrapper>
      <Modal>
        <StyledModalCard data-aid="viewOriginalHtml-modal" onClose={onClose}>
          <Close onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </Close>
          {/* An empty sandbox is every restriction: no scripts, no navigation, and an opaque origin
              (which is why attachment URLs are resolved outside). That attribute is the enforcement. */}
          <StyledFrame id="view-original-html" sandbox="" srcDoc={convertedHtml} width="100%" />
        </StyledModalCard>
      </Modal>
    </PortalModalWrapper>
  );
}

HtmlContentModal.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.instanceOf(Attachment)),
  headers: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.arrayOf(PropTypes.string),
    cc: PropTypes.arrayOf(PropTypes.string),
    bcc: PropTypes.arrayOf(PropTypes.string),
    subject: PropTypes.string,
    timestamp: PropTypes.string,
  }),
  html: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onFetchAttachmentUrls: PropTypes.func,
  orgId: PropTypes.string,
};

const StyledModalCard = styled(ModalCard)`
  height: 80%;
  width: 80%;
`;

const StyledFrame = styled.iframe`
  border: none;
  height: 100%;
`;

// A srcdoc document starts with no stylesheet, so mail that sets no fonts falls back to the browser
// default (Times). Like Gmail, give it the app's base typography; mail with its own styles still wins.
// The document is a separate origin and cannot read our CSS variables or load the Inter webfont, so the
// values are resolved here and the stack falls through to Arial.
// The background and color-scheme are resolved with the text colors: an iframe paints its own white canvas
// unless told otherwise, so themed text without them is light-on-white in the dark theme.
export function wrapEmailDocument(
  bodyHtml,
  { background, color, colorScheme, fontSize, ruleColor, labelColor, mutedColor },
  headers
) {
  return `<!doctype html><html><head><meta charset="utf-8"><style>
html { color-scheme: ${colorScheme}; }
body { background: ${background}; color: ${color}; font-family: Inter, Arial, sans-serif; font-size: ${fontSize}; line-height: 1.4; margin: 0; overflow-wrap: break-word; }
img { max-width: 100%; }
.envelope { border-bottom: 1px solid ${ruleColor}; font-size: 12px; margin-bottom: 12px; padding-bottom: 12px; text-align: left; }
.envelope div { color: ${mutedColor}; }
.envelope b { color: ${labelColor}; }
.envelope .subject { color: inherit; font-size: 1.2em; font-weight: bold; margin-top: 8px; }
</style></head><body>${envelopeHtml(headers)}${bodyHtml}</body></html>`;
}

// The envelope scrolls with the body, so it is part of the sandboxed document. Every value is
// escaped: these are addresses and a subject the org did not author.
function envelopeHtml(headers) {
  if (!headers) return '';
  const row = (label, value) => (value ? `<div><b>${escapeHtml(label)}</b> ${escapeHtml(value)}</div>` : '');
  const date = headers.timestamp ? moment(headers.timestamp).format('MMM D, YYYY h:mma') : '';
  return `<div class="envelope" data-aid="viewOriginalHtml-headers">${row(i18n.t('From:'), headers.from)}${row(
    i18n.t('To:'),
    (headers.to || []).join(', ')
  )}${row(i18n.t('Cc:'), (headers.cc || []).join(', '))}${row(i18n.t('Bcc:'), (headers.bcc || []).join(', '))}${row(
    i18n.t('Date:'),
    date
  )}${headers.subject ? `<div class="subject">${escapeHtml(headers.subject)}</div>` : ''}</div>`;
}

export function readBaseTypography() {
  const root = typeof document !== 'undefined' && document.documentElement;
  const read = (name, fallback) => (root && getComputedStyle(root).getPropertyValue(name).trim()) || fallback;
  return {
    background: read('--color-default-background-color', '#fff'),
    color: read('--color-gray900', '#242424'),
    colorScheme: read('color-scheme', 'normal'),
    fontSize: read('--font-size-base', '13px'),
    ruleColor: read('--color-gray300', '#ddd'),
    labelColor: read('--color-gray800', '#333'),
    mutedColor: read('--color-gray600', '#666'),
  };
}

// Inline images arrive either as `cid:` references (received mail) or already pointing at the
// attachment route (mail composed in the desktop). Both become the signed URL once it's known.
export function resolveImageSources(orgId, html, attachments, urlsById = {}) {
  const srcUrls = {};
  _.forEach(attachments, attachment => {
    const apiUrl = getAttachmentUrl('email', orgId, attachment);
    const srcUrl = urlsById[attachment.id] || apiUrl;
    srcUrls[apiUrl] = srcUrl;
    if (attachment.contentId) {
      srcUrls[`cid:${attachment.contentId}`] = srcUrl;
    }
  });

  // Rewriting the sanitized DOM rather than the markup covers every way an attribute can be written.
  const body = DOMPurify.sanitize(html || '', { RETURN_DOM: true });
  _.forEach(body.querySelectorAll('img[src]'), img => {
    const srcUrl = srcUrls[img.getAttribute('src')];
    if (srcUrl) img.setAttribute('src', srcUrl);
  });
  return body.innerHTML;
}

const Close = styled.div`
  cursor: pointer;
  font-size: var(--font-size-medium);
  padding: 8px 12px;
  position: absolute;
  right: 0px;
  top: 0px;

  &:hover {
    color: ${p => p.theme.colors.gray600};
  }
`;

const Modal = styled.div`
  text-align: center;
  .modalCard-content {
    padding: ${p => p.theme.spacing.large};
    position: relative;
  }
`;
